import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { DriverData } from './types';
import { ImageGalleryModal } from '../../components/ImageGalleryModal';
import { baseUrl } from '../../customDataProvider';

// Canadian provinces and their codes
const CANADIAN_PROVINCES = [
  { code: 'ON', name: 'Ontario' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'AB', name: 'Alberta' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'QC', name: 'Quebec' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' },
];

interface License {
  id: number;
  licenseTypeId: number;
  code: string;
  expiry: string;
  status: string;
  fileId?: number;
  file?: any;
  licenseType?: any;
}

interface LicenceTabProps {
  driverData: DriverData | null;
  licenseTypes?: any[];
  onRefresh?: () => void;
}

const licenseStatuses = ['ACTIVE', 'EXPIRED', 'SUSPENDED', 'REVOKED'];

// Province-specific license code validation patterns
const PROVINCE_VALIDATION_PATTERNS: Record<string, { pattern: RegExp; format: string }> = {
  ON: { pattern: /^[A-Za-z]{1}[0-9]{4}(-[0-9]{5}){2}$/, format: 'L0000-00000-00000' },
  BC: { pattern: /^[0-9]{7}$/, format: '0000000' },
  AB: { pattern: /^[0-9]{6}-[0-9]{3}$/, format: '000000-000' },
  MB: { pattern: /^[0-9]{7}$/, format: '0000000' },
  SK: { pattern: /^[0-9]{7}$/, format: '0000000' },
  QC: { pattern: /^[A-Za-z]{1}[0-9]{4}-[0-9]{6}-[0-9]{2}$/, format: 'L0000-000000-00' },
  NB: { pattern: /^[0-9]{7}$/, format: '0000000' },
  NS: { pattern: /^[0-9]{7}$/, format: '0000000' },
  PE: { pattern: /^[0-9]{7}$/, format: '0000000' },
  NL: { pattern: /^[0-9]{7}$/, format: '0000000' },
  NT: { pattern: /^[0-9]{7}$/, format: '0000000' },
  NU: { pattern: /^[0-9]{7}$/, format: '0000000' },
  YT: { pattern: /^[0-9]{7}$/, format: '0000000' },
};

export const LicencesTab: React.FC<LicenceTabProps> = ({ driverData, licenseTypes = [], onRefresh }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const imageGalleryRef = useRef<any>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<string>('');
  const [formData, setFormData] = useState({
    licenseTypeId: '',
    code: '',
    expiry: '',
    status: 'ACTIVE',
    file: null as File | null,
  });
  const [fileName, setFileName] = useState<string>('');
  const [existingFileId, setExistingFileId] = useState<number | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<number | null>(null);
  const [deleteExistingFile, setDeleteExistingFile] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [filteredLicenseTypes, setFilteredLicenseTypes] = useState<any[]>([]);

  const licenses = (driverData?.licenses || []) as License[];
  const openImageViewer = (license: License) => {
    if (!license.fileId || !license.file?.mimeType?.startsWith('image/')) return;
    setGalleryImages([
      {
        id: license.fileId,
        thumbnail: license.file.thumbnail,
        mimeType: license.file.mimeType,
      },
    ]);
    setGalleryIndex(0);
    setGalleryOpen(true);
    // Also support imperative open in case the modal uses ref
    imageGalleryRef.current?.handleOpenModal(0);
  };

  // Filter license types by province and sort alphabetically
  useEffect(() => {
    const filtered = licenseTypes
      .filter(lt => lt.province === selectedProvince)
      .sort((a, b) => a.name.localeCompare(b.name));
    setFilteredLicenseTypes(filtered);
    // Only reset license type selection when province changes in add mode (not editing)
    if (!editingLicense) {
      setFormData(prev => ({ ...prev, licenseTypeId: '' }));
    }
  }, [selectedProvince, licenseTypes, editingLicense]);

  const handleAddLicense = () => {
    setEditingLicense(null);
    setSelectedProvince('');
    setFormData({
      licenseTypeId: '',
      code: '',
      expiry: '',
      status: 'ACTIVE',
      file: null,
    });
    setFileName('');
    setExistingFileId(null);
    setUploadedFileId(null);
    setDeleteExistingFile(false);
    setDialogOpen(true);
  };

  const handleEditLicense = (license: License) => {
    setEditingLicense(license);
    // Look up the province from licenseTypes array using licenseTypeId
    const licenseType = licenseTypes.find(lt => lt.id === license.licenseTypeId);
    const province = licenseType?.province || 'ON';
    setSelectedProvince(province);

    // Format expiry date to YYYY-MM-DD for date input
    let formattedExpiry = '';
    if (license.expiry) {
      const date = new Date(license.expiry);
      formattedExpiry = date.toISOString().split('T')[0];
    }

    setFormData({
      licenseTypeId: license.licenseTypeId.toString(),
      code: license.code,
      expiry: formattedExpiry,
      status: license.status,
      file: null,
    });
    setFileName(license.file?.name || '');
    setExistingFileId(license.fileId || null);
    setUploadedFileId(null);
    setDeleteExistingFile(false);
    setDialogOpen(true);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    // Step 1: Upload file to /files endpoint
    try {
      setIsUploadingFile(true);
      const result = await dataProvider.create('files', {
        data: {
          file,
          name: file.name,
          folder: 'driver-licenses',
          description: `License file for driver ${driverData?.id}`,
        },
      });

      // Step 2: Store the fileId (not the raw file)
      const uploadedFileIdFromResponse = result.data?.id;
      setUploadedFileId(uploadedFileIdFromResponse);
      setFileName(file.name);
      setFormData({ ...formData, file: null }); // Clear raw file from form
      notify('File uploaded successfully', { type: 'success' });
    } catch (error: any) {
      notify('Error uploading file: ' + (error.message || 'Unknown error'), { type: 'error' });
      setIsUploadingFile(false);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const validateLicenseCode = (code: string, province: string): boolean => {
    if (!code) return false;
    const validation = PROVINCE_VALIDATION_PATTERNS[province];
    if (!validation) return true; // No validation pattern defined
    return validation.pattern.test(code);
  };

  const getProvinceValidationFormat = (province: string): string => {
    return PROVINCE_VALIDATION_PATTERNS[province]?.format || 'Check provincial guidelines';
  };

  const handleSaveLicense = async () => {
    if (!selectedProvince) {
      notify('Please select a province first', { type: 'warning' });
      return;
    }

    if (!formData.licenseTypeId || !formData.code) {
      notify('Please fill in all required fields', { type: 'warning' });
      return;
    }

    // Validate license code format
    if (!validateLicenseCode(formData.code, selectedProvince)) {
      const expectedFormat = getProvinceValidationFormat(selectedProvince);
      notify(`Invalid license code format for ${selectedProvince}. Expected format: ${expectedFormat}`, { type: 'error' });
      return;
    }

    try {
      if (editingLicense) {
        const updateData: any = {
          licenseTypeId: parseInt(formData.licenseTypeId as string),
          code: formData.code,
          expiry: formData.expiry,
          status: formData.status,
        };

        // Include fileId: either the newly uploaded one or the existing one (if not deleting)
        if (uploadedFileId) {
          updateData.fileId = uploadedFileId;
        } else if (existingFileId && !deleteExistingFile) {
          updateData.fileId = existingFileId;
        }

        // Flag to delete the existing file
        if (deleteExistingFile) {
          updateData.deleteFile = true;
        }

        await dataProvider.update('drivers/licenses', {
          id: editingLicense.id,
          data: updateData,
          previousData: editingLicense,
        });
        notify('License updated successfully', { type: 'success' });
      } else {
        const createData: any = {
          driverId: driverData?.id,
          licenseTypeId: parseInt(formData.licenseTypeId as string),
          code: formData.code,
          expiry: formData.expiry,
          status: formData.status,
        };

        // Include the uploaded fileId if a file was uploaded
        if (uploadedFileId) {
          createData.fileId = uploadedFileId;
        }

        await dataProvider.create('drivers/licenses', {
          data: createData,
        });
        notify('License added successfully', { type: 'success' });
      }
      setDialogOpen(false);
      onRefresh?.();
    } catch (error: any) {
      notify('Error saving license: ' + (error.message || 'Unknown error'), { type: 'error' });
    }
  };

  const handleDeleteLicense = async (license: License) => {
    if (window.confirm('Are you sure you want to delete this license?')) {
      try {
        await dataProvider.delete('drivers/licenses', { id: license.id });
        notify('License deleted successfully', { type: 'success' });
        onRefresh?.();
      } catch (error: any) {
        notify('Error deleting license: ' + (error.message || 'Unknown error'), { type: 'error' });
      }
    }
  };

  const handleDialogClose = () => {
    setSelectedProvince('');
    setFormData({
      licenseTypeId: '',
      code: '',
      expiry: '',
      status: 'ACTIVE',
      file: null,
    });
    setFileName('');
    setExistingFileId(null);
    setUploadedFileId(null);
    setDeleteExistingFile(false);
    setDialogOpen(false);
  };

  const getLicenseTypeName = (typeId: number) => {
    return licenseTypes.find(lt => lt.id === typeId)?.name || `License Type ${typeId}`;
  };

  const getProvinceFromLicense = (license: License): string => {
    // Look up the province from licenseTypes array using licenseTypeId
    const licenseType = licenseTypes.find(lt => lt.id === license.licenseTypeId);
    return licenseType?.province || 'ON';
  };

  const getProvinceName = (code?: string) => {
    if (!code) return 'Not specified';
    return CANADIAN_PROVINCES.find(p => p.code === code)?.name || code;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Driving Licenses</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={handleAddLicense}
        >
          Add License
        </Button>
      </Box>

      {/* Table */}
      {licenses.length > 0 ? (
        <Table size="small" sx={{ mb: 4 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Province</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenses.map(license => (
              <TableRow
                key={license.id}
                sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
              >
                <TableCell>{getProvinceName(getProvinceFromLicense(license))}</TableCell>
                <TableCell>{getLicenseTypeName(license.licenseTypeId)}</TableCell>
                <TableCell>{license.code}</TableCell>
                <TableCell>
                  {license.expiry ? new Date(license.expiry).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>{license.status}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEditLicense(license)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No licenses added yet
        </Typography>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{editingLicense ? 'Edit License' : 'Add License'}</Typography>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, overflow: 'visible', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1' }, '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '6px' }, '&::-webkit-scrollbar-thumb:hover': { background: '#555' } }}>
          <Stack spacing={2}>
            {/* Province Selector */}
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel>Province/State *</InputLabel>
              <Select
                value={selectedProvince}
                onChange={(e) => setSelectedProvince(e.target.value)}
                label="Province/State *"
              >
                {CANADIAN_PROVINCES.map(prov => (
                  <MenuItem key={prov.code} value={prov.code}>
                    {prov.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* License Type - Cascaded by Province */}
            <FormControl fullWidth size="small" disabled={!selectedProvince}>
              <InputLabel>License Type *</InputLabel>
              <Select
                value={formData.licenseTypeId}
                onChange={(e) => setFormData({ ...formData, licenseTypeId: e.target.value })}
                label="License Type *"
              >
                <MenuItem value="">
                  {selectedProvince ? 'Select license type' : 'Select province first'}
                </MenuItem>
                {filteredLicenseTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name} - {type.longDescription}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* License Code with Format Hint */}
            <MuiTextField
              size="small"
              label="License Code *"
              fullWidth
              disabled={!selectedProvince}
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder={selectedProvince ? `Format: ${getProvinceValidationFormat(selectedProvince)}` : 'Select province first'}
              helperText={selectedProvince ? `Expected format: ${getProvinceValidationFormat(selectedProvince)}` : 'Select a province to see license code format'}
            />

            <MuiTextField
              size="small"
              label="Expiry Date"
              type="date"
              fullWidth
              value={formData.expiry}
              onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                {licenseStatuses.map(status => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Attach License Image (Optional)
              </Typography>

              {/* Show existing file if editing and file exists but not deleted */}
              {editingLicense && existingFileId && !deleteExistingFile && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">📄 {fileName}</Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setDeleteExistingFile(true);
                      setFileName('');
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              )}

              {/* Show message if file is marked for deletion */}
              {deleteExistingFile && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#ffebee', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'error.main' }}>File will be deleted</Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setDeleteExistingFile(false);
                      setFileName(editingLicense?.file?.name || '');
                    }}
                  >
                    Undo
                  </Button>
                </Box>
              )}

              {/* Show uploaded file */}
              {uploadedFileId && !deleteExistingFile && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>✓ {fileName}</Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setUploadedFileId(null);
                      setFileName('');
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              )}

              {/* File upload area - only show if no file has been uploaded yet and no existing file (or existing file marked for deletion) */}
              {!uploadedFileId && (!existingFileId || deleteExistingFile) && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: isUploadingFile ? 'wait' : 'pointer',
                    backgroundColor: isUploadingFile ? '#f0f0f0' : '#fafafa',
                    '&:hover': { backgroundColor: isUploadingFile ? '#f0f0f0' : '#f5f5f5' },
                    border: '2px dashed #ccc',
                    opacity: isUploadingFile ? 0.6 : 1,
                    pointerEvents: isUploadingFile ? 'none' : 'auto',
                  }}
                >
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    disabled={isUploadingFile}
                    style={{ display: 'none' }}
                    id="license-file-input"
                  />
                  <label htmlFor="license-file-input" style={{ cursor: isUploadingFile ? 'wait' : 'pointer', display: 'block' }}>
                    {isUploadingFile ? (
                      <Typography variant="body2" color="text.secondary">
                        Uploading file...
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click to select a file (PDF, DOC, DOCX, JPG, PNG)
                      </Typography>
                    )}
                  </label>
                </Paper>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              handleDeleteLicense(editingLicense!);
              handleDialogClose();
            }}
            variant="outlined"
            color="error"
            sx={{ visibility: editingLicense ? 'visible' : 'hidden' }}
          >
            Delete
          </Button>
          <Box>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button onClick={handleSaveLicense} variant="contained">
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Image Viewer */}
      <ImageGalleryModal
        ref={imageGalleryRef}
        images={galleryImages}
        imageDefaultIndex={galleryIndex}
        openModal={galleryOpen}
        setOpenModal={setGalleryOpen}
      >
        <></>
      </ImageGalleryModal>
    </Box>
  );
};
